// @ts-check
// Mesclar registros (doc 10, seção 13.3): abas "Prévia da mescla", uma por registro (1º, 2º...) e "Configurações" com o
// registro que tem prioridade nos conflitos. A prévia vem do servidor (dryRun), com as mesmas regras da gravação.
import { html } from 'htm/preact';
import { useEffect, useState } from 'preact/hooks';
import { objectsByNameSingular } from '../../data/metadata-store.js';
import { findManyRecords } from '../../data/rest.js';
import { mergeRecords } from '../../data/parity.js';
import { navigate } from '../../app/router.js';
import { getRecordLabel, getRelationIdFieldName, isFieldDisplayable, isManyToOneRelation, isOneToManyRelation } from '../../lib/field-metadata.js';
import { FieldDisplay } from '../object-record/field-display.js';
import { Icon } from '../../ui/icon.js';
import { Button, IconButton } from '../../ui/button.js';
import { Skeleton } from '../../ui/feedback.js';
import { TabList } from '../../ui/tab-list.js';
import { showErrorSnackbar, showSuccessSnackbar } from '../../ui/snackbar.js';
import { closeSidePanel } from './side-panel-state.js';
import { t, translateMetadataLabel } from '../../i18n/index.js';

const HIDDEN_FIELD_NAMES = new Set(['id', 'position', 'searchVector', 'deletedAt', 'updatedAt', 'createdBy', 'updatedBy']);

/**
 * @param {{ objectMetadata: import('../../lib/field-metadata.js').ObjectMetadata, record: any }} props
 */
function MergeRecordFields({ objectMetadata, record }) {
  const fields = objectMetadata.fields
    .filter((field) => isFieldDisplayable(field) && !isOneToManyRelation(field) && !HIDDEN_FIELD_NAMES.has(field.name))
    .sort((first, second) => (first.position ?? 0) - (second.position ?? 0));
  return html`<div class="merge-record-fields">
    <div class="merge-record-title">${getRecordLabel(objectMetadata, record) || t('record.untitled')}</div>
    ${fields.map((field) => html`<div class="merge-record-field" key=${field.id}>
      <span class="merge-record-field-label">${translateMetadataLabel(field.label)}</span>
      <span class="merge-record-field-value"><${FieldDisplay} field=${field} record=${record} emptyLabel=${t('parity.merge.empty')} /></span>
    </div>`)}
  </div>`;
}

/**
 * A prévia do servidor traz só o id das relações; o registro relacionado (para o chip) vem de um dos originais.
 * @param {import('../../lib/field-metadata.js').ObjectMetadata} objectMetadata @param {any} preview @param {any[]} records
 */
function withExpandedRelations(objectMetadata, preview, records) {
  const enriched = { ...preview };
  for (const field of objectMetadata.fields) {
    if (!isManyToOneRelation(field)) continue;
    const relatedId = preview[getRelationIdFieldName(field)];
    enriched[field.name] = relatedId ? records.find((record) => record[field.name]?.id === relatedId)?.[field.name] ?? null : null;
  }
  return enriched;
}

/**
 * @param {{ objectNameSingular: string, ids: string[], onDone?: (recordId: string) => void }} props
 */
export function MergeRecordsPanel({ objectNameSingular, ids, onDone }) {
  const objectMetadata = objectsByNameSingular.value.get(objectNameSingular);
  const [records, setRecords] = useState(/** @type {any[] | null} */ (null));
  const [priorityIndex, setPriorityIndex] = useState(0);
  const [preview, setPreview] = useState(/** @type {any | null} */ (null));
  const [activeTab, setActiveTab] = useState('preview');
  const [isMerging, setIsMerging] = useState(false);

  useEffect(() => {
    if (!objectMetadata) return;
    findManyRecords(objectMetadata, { filter: { field: 'id', operator: 'in', value: ids }, limit: ids.length, depth: 1 })
      .then(({ records: loaded }) => setRecords(ids.map((id) => loaded.find((record) => record.id === id)).filter(Boolean)))
      .catch(showErrorSnackbar);
  }, [objectNameSingular, ids.join(',')]);

  useEffect(() => {
    if (!objectMetadata) return undefined;
    let isCancelled = false;
    setPreview(null);
    mergeRecords(objectMetadata, ids, { conflictPriorityIndex: priorityIndex, dryRun: true })
      .then((result) => !isCancelled && setPreview(result.record))
      .catch((error) => !isCancelled && showErrorSnackbar(error));
    return () => {
      isCancelled = true;
    };
  }, [objectNameSingular, ids.join(','), priorityIndex]);

  if (!objectMetadata) return null;

  const merge = async () => {
    setIsMerging(true);
    try {
      const result = await mergeRecords(objectMetadata, ids, { conflictPriorityIndex: priorityIndex });
      showSuccessSnackbar(t('parity.merge.done', { count: ids.length }));
      closeSidePanel();
      onDone?.(result.record.id);
      navigate(`/object/${objectMetadata.nameSingular}/${result.record.id}`);
    } catch (error) {
      showErrorSnackbar(error);
    } finally {
      setIsMerging(false);
    }
  };

  const tabs = [
    { id: 'preview', title: t('parity.merge.preview'), icon: 'IconArrowMerge' },
    ...ids.map((_id, index) => ({ id: `record-${index}`, title: t(`parity.merge.ordinals.${index}`), icon: index === 0 ? 'IconSquareNumber1' : 'IconSquareNumber9' })),
    { id: 'settings', title: t('parity.merge.settings'), icon: 'IconSettings' },
  ];
  const recordIndex = activeTab.startsWith('record-') ? Number(activeTab.slice('record-'.length)) : -1;

  return html`<div class="side-panel-page merge-records-panel">
    <div class="side-panel-top-bar">
      <div class="side-panel-top-bar-left">
        <span class="side-panel-page-chip">
          <${Icon} name="IconArrowMerge" size=${16} />
          <span>${t('parity.merge.title', { object: translateMetadataLabel(objectMetadata.labelPlural) })}</span>
        </span>
      </div>
      <${IconButton} icon="IconX" size="small" variant="tertiary" ariaLabel=${t('sidePanel.close')} onClick=${closeSidePanel} />
    </div>
    <div class="merge-records-tabs"><${TabList} tabs=${tabs} activeTabId=${activeTab} onChange=${setActiveTab} /></div>
    <div class="side-panel-content merge-records-content">
      ${activeTab === 'settings'
        ? html`<div class="merge-settings">
            <span class="form-field-label">${t('parity.merge.conflicts')}</span>
            ${ids.map((_id, index) => html`<label class="merge-priority-option" key=${index}>
              <input type="radio" name="merge-priority" checked=${priorityIndex === index} onChange=${() => setPriorityIndex(index)} />
              <span>${t('parity.merge.priority', { ordinal: t(`parity.merge.ordinals.${index}`) })}</span>
              ${records?.[index] ? html`<span class="merge-priority-label">${getRecordLabel(objectMetadata, records[index])}</span>` : null}
            </label>`)}
            <p class="create-record-hint">${t('parity.merge.rulesHint')}</p>
          </div>`
        : recordIndex >= 0
          ? records?.[recordIndex]
            ? html`<${MergeRecordFields} objectMetadata=${objectMetadata} record=${records[recordIndex]} />`
            : html`<${Skeleton} width="80%" height=${16} />`
          : preview
            ? html`<p class="create-record-hint">${t('parity.merge.previewHint', { ordinal: t(`parity.merge.ordinals.${priorityIndex}`) })}</p>
                <${MergeRecordFields} objectMetadata=${objectMetadata} record=${withExpandedRelations(objectMetadata, preview, records ?? [])} />`
            : html`<div class="merge-record-fields">${[0, 1, 2, 3].map((index) => html`<${Skeleton} key=${index} width="90%" height=${16} />`)}</div>`}
    </div>
    <div class="side-panel-footer">
      <${Button} title=${t('common.cancel')} variant="secondary" size="small" onClick=${closeSidePanel} />
      <${Button} title=${isMerging ? t('parity.merge.merging') : t('parity.merge.submit')} icon="IconArrowMerge" accent="blue" size="small"
        disabled=${!preview} isLoading=${isMerging} onClick=${merge} />
    </div>
  </div>`;
}
